import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { useRouter } from '@eneco/next';
import { Box, Grid, IconButton, NotificationBox, Stack, Stretch, Text } from '@eneco/sparky';
import { ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import type { NotificationBarProps } from '@eneco/sparky/types';

import type { PaginatedContentContent } from './paginated-content-content.types';
import { PAGINATED_CONTENT_SLOTS, resolveSlot } from './slots';

const indexSearchParam = 'page';

/**
 * Determines which page numbers to display, and where to place ellipsis. Ported verbatim from the
 * legacy `getControls` (`libs/sitecore/components/content/src/PaginatedContent/Components/
 * getControls.ts`) — a pure algorithm with no CMS dependency.
 */
const getControls = (indexLimit: number, currentIndex: number): Array<number | '...'> => {
  if (indexLimit <= 4) return Array.from({ length: indexLimit + 1 }, (_, i) => i + 1);
  const userFacingIndexLimit = indexLimit + 1;
  if (currentIndex <= 1) return [1, 2, 3, '...', userFacingIndexLimit];
  if (currentIndex === 2) return [1, 2, 3, 4, '...', userFacingIndexLimit];
  if (currentIndex === indexLimit - 2) return [1, '...', userFacingIndexLimit - 3, userFacingIndexLimit - 2, userFacingIndexLimit - 1, userFacingIndexLimit];
  if (currentIndex >= indexLimit - 1) return [1, '...', userFacingIndexLimit - 2, userFacingIndexLimit - 1, userFacingIndexLimit];
  const userFacingIndex = currentIndex + 1;
  return [1, '...', userFacingIndex - 1, userFacingIndex, userFacingIndex + 1, '...', userFacingIndexLimit];
};

/**
 * `PaginatedContent` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Known simplification — see `paginated-content-content.types.ts`: the legacy article list comes from a
 * direct Sitecore Content Services GraphQL query, infrastructure this lane's module boundary cannot and
 * should not reach. This ports the parts that genuinely are CMS content — the error notification, the
 * `jss-paginated-content-footer` placeholder, and the pagination controls — faithfully from the legacy
 * component (`libs/sitecore/components/content/src/PaginatedContent/PaginatedContent.tsx` and its
 * `Controls.tsx`). The article grid itself is not reproduced: with no data source wired up, rendering an
 * always-empty `Articles` grid would add code with no behaviour, so it is omitted rather than faked.
 */
const PaginatedContent: FC = () => {
  const { adapter } = useCms();
  const content = useContent<PaginatedContentContent>();
  const rendering = useRendering();
  const router = useRouter();

  const errorNotification = content.notifications?.find(entry => entry.role === 'errorNotification') ?? content.errorNotification;
  const errorHtml = adapter.toRichText(errorNotification?.content);

  const footerSlot = resolveSlot(rendering, PAGINATED_CONTENT_SLOTS.footer);

  // No article data source is wired up (see the doc comment above), so pagination never has more than
  // one page today — indexLimit stays undefined and Controls renders nothing, matching what the legacy
  // Controls.tsx itself does when `indexLimit` is falsy.
  const indexLimit: number | undefined = undefined;
  const currentIndex = 0;
  const goToPage = (userFacingIndex: number) => router.push(`${router.activePath}?${indexSearchParam}=${userFacingIndex}`);

  return (
    <Stack>
      <Box paddingBottom={{ initial: '8', md: '16' }}>
        {errorNotification ? (
          <Stack.Item>
            <NotificationBox
              isAlert
              text={errorHtml ? <RichTextComponent html={errorHtml} /> : undefined}
              title={errorNotification.title}
              variant={(errorNotification.variant as NotificationBarProps['variant']) ?? 'error'}
            />
          </Stack.Item>
        ) : (
          <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap={{ initial: '10', md: '16' }} />
        )}
      </Box>
      {footerSlot ? <Placeholder name={footerSlot} /> : null}
      {!!indexLimit && indexLimit >= 1 && (
        <Stretch>
          <Box paddingY="16" as="nav" aria-label={content.navAriaLabel}>
            <Stack gap="1" alignX="center" alignY="center" direction="row" as="ul">
              {currentIndex > 0 && (
                <li>
                  <IconButton label={content.previousButtonLabel ?? ''} onClick={() => goToPage(currentIndex)}>
                    <ChevronLeftIcon />
                  </IconButton>
                </li>
              )}
              {getControls(indexLimit, currentIndex).map((controlItem, index) =>
                controlItem === '...' ? (
                  <Text as="li" aria-hidden="true" key={`ellipsis-${index}`}>
                    ...
                  </Text>
                ) : (
                  <li key={controlItem}>
                    <IconButton
                      label={(content.pageLinkLabel ?? '').replace('{index}', String(controlItem))}
                      isCurrent={controlItem === currentIndex + 1}
                      onClick={() => goToPage(controlItem)}>
                      {controlItem}
                    </IconButton>
                  </li>
                ),
              )}
              {currentIndex < indexLimit && (
                <li>
                  <IconButton label={content.nextButtonLabel ?? ''} onClick={() => goToPage(currentIndex + 2)}>
                    <ChevronRightIcon />
                  </IconButton>
                </li>
              )}
            </Stack>
          </Box>
        </Stretch>
      )}
    </Stack>
  );
};

export default PaginatedContent;
